type ProcessImageResult = {
  error: boolean;
  message?: string;
  isLoading: boolean;
};

/**
 * processImage
 * Sends the image to the worker and triggers the color correction process.
 *
 * This function:
 * - Creates a temporary canvas to extract ImageData from the HTMLImageElement
 * - Sends the image data and parameters to the Web Worker for processing
 * - Returns the initial color correction state
 *
 * @param image The image to process
 * @param worker The worker instance
 * @param memoryRespawnThreshold The memory limit in MB before forcing a worker respawn
 * @param processDarkImagesOverride Whether to override the default behavior of not processing super dark images
 * @returns The initial color correction state
 */
export function processImage(
  image: HTMLImageElement,
  worker: Worker | null,
  memoryRespawnThreshold?: number,
  processDarkImagesOverride?: boolean
): ProcessImageResult {
  if (!worker) {
    return {
      message: 'Color correction worker not initialized.',
      error: true,
      isLoading: false,
    };
  }

  // Create a temporary canvas to extract ImageData from the image
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return {
      message:
        'Color correction failed to get canvas context for image processing.',
      error: true,
      isLoading: false,
    };
  }
  let imageData: ImageData;
  try {
    ctx.drawImage(image, 0, 0);
    imageData = ctx.getImageData(0, 0, image.width, image.height);
  } catch (error) {
    console.error(error);
    const errorMessage =
      'Color correction failed to get image data from the image.';
    return {
      message: errorMessage,
      error: true,
      isLoading: false,
    };
  }

  // Send the image and parameters to the worker for processing
  worker.postMessage({
    action: 'processImage',
    imageData: imageData,
    params: {
      memoryRespawnThreshold: memoryRespawnThreshold || null,
      processDarkImagesOverride,
    },
  });

  return {
    message: 'Color correction is processing the image.',
    error: false,
    isLoading: true,
  };
}
